import type { LocationType } from "../../__generated__/types.gqlgenerated";
import type { ApolloClient } from "@apollo/client";

import {
  LocationAutocompleteDocument,
  type LocationAutocompleteQuery,
  type LocationAutocompleteQueryVariables,
} from "../graphQL/__generated__/locationAutocomplete.gqlgenerated";

// Type definitions for location data from GraphQL
interface LocationWithCode {
  id: string;
  name: string;
  code: string;
}

interface LocationBasic {
  id: string;
  name: string;
}

export interface Gen2Location {
  latitude: number;
  longitude: number;
  precisionKm: number;
  country: LocationWithCode;
  admin1?: LocationWithCode | null;
  admin2?: LocationWithCode | null;
  admin3?: LocationWithCode | null;
  admin4?: LocationWithCode | null;
  admin5?: LocationWithCode | null;
  admin6?: LocationWithCode | null;
  postalCode?: LocationBasic | null;
  preciseLocation?: LocationBasic | null;
}

export interface LocationSuggestion {
  suggestion: string;
  gen2Location: Gen2Location;
}

/**
 * Fetches location autocomplete suggestions using GraphQL
 *
 * @param client - Apollo Client instance
 * @param country - Country code (e.g., 'US')
 * @param language - Language code (e.g., 'en')
 * @param query - Search text from user input
 * @param maxResults - Maximum number of suggestions to return
 * @param locationTypeFilter - Optional filter for location types
 * @returns Array of location suggestions with metadata
 */
export const fetchLocationAutocompleteSuggestionsViaGraphql = async (
  client: ApolloClient<object>,
  country: string,
  language: string,
  query: string,
  maxResults = 6,
  locationTypeFilter?: LocationType[],
): Promise<LocationSuggestion[]> => {
  try {
    const result = await client.query<
      LocationAutocompleteQuery,
      LocationAutocompleteQueryVariables
    >({
      query: LocationAutocompleteDocument,
      variables: {
        searchText: query,
        country: country.toUpperCase(),
        language: language.toLowerCase(),
        maxResults,
        locationTypeFilter: locationTypeFilter || [],
        useIp: false,
        exactMatchOnly: false,
      },
      // Use cache-first for autocomplete to avoid redundant API calls
      fetchPolicy: "cache-first",
    });

    if (!result.data?.locationAutocomplete) {
      return [];
    }

    // Transform GraphQL response to simplified format
    return result.data.locationAutocomplete.map((item) => ({
      suggestion: item.formattedName,
      gen2Location: item.gen2Location,
    }));
  } catch (error) {
    console.error("Location autocomplete GraphQL error:", error);
    throw error;
  }
};
